/**
 * What each streamed event does to a chat — for every chat.
 *
 * Enterprise UI behaviour:
 * - tool_call / tool_result never become chat bubbles (no tool spam), but
 *   tool_call DOES drive the collapsible progress feed — it's the only
 *   guaranteed signal, since the model's narration is a prompt rule it may skip
 * - every SSE event touches streamingLastEventAt (idle / escalation copy in UI)
 * - query_result creates a workspace tab and attaches a factual receipt
 *   ("1,248 rows × 6 columns · Snowflake · 3.2s") to the step that produced it
 * - agent_message streams clean prose into chat
 * - sql_approval pauses the turn for an approval card (also used for API/file requests)
 * - voice_call_approval gates a real phone call
 * - definitions_learned / definitions_used are placed right under the answer
 *   they came with, even though they arrive before it is finished
 *
 * Each function takes `get`, the chat store's getter, so the same code drives
 * the Copilot page's store and every docked chat's own store.
 */
import type {
  QueryResultTab,
  ChartArtifact,
  MetricArtifact,
  DocumentArtifact,
  CalculationAuditReport,
  CopilotMessage,
} from '../../pages/copilot/stores/useCopilotStore';
import { toVoiceCallSummary } from '../../services/voiceCallService';
import { toDownloadOffer } from '../../types/download';
import { toWorkbook } from '../../types/workbook';
import { toDefinitionsUsed, toLearnedItems } from '../../types/definitions';
import type { ChatSlice } from './chatSlice';

export type ChatGet = () => ChatSlice;

// Map tool names → user-friendly action labels for the streaming indicator
const TOOL_ACTION_LABELS: Record<string, string> = {
  explore_schema: 'Exploring your data…',
  propose_sql: 'Writing query…',
  execute_sql: 'Fetching data…',
  propose_chart: 'Building chart…',
  propose_metric_card: 'Building metric card…',
  search_context: 'Checking business context…',
  get_column_profile: 'Checking data…',
  save_finding: 'Saving finding…',
  analyze_data: 'Crunching numbers…',
  forecast_from_frame: 'Running forecast…',
  capture_definition: 'Learning definition…',
  call_carrier: 'On a call…',
  get_lead_to_cash_snapshot: 'Loading chain snapshot…',
  get_l2c_stage_detail: 'Loading stage detail…',
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * One-line factual summary of a query result: "1,248 rows × 6 columns ·
 * Snowflake · 3.2s". Deliberately free of any model involvement so it can be
 * shown the moment the data lands.
 */
function formatResultReceipt(d: any): string {
  const parts: string[] = [];

  const rowCount = typeof d?.row_count === 'number' ? d.row_count : null;
  const colCount = Array.isArray(d?.columns) ? d.columns.length : 0;
  if (rowCount !== null) {
    const rows = `${rowCount.toLocaleString()} row${rowCount === 1 ? '' : 's'}`;
    parts.push(colCount ? `${rows} × ${colCount} column${colCount === 1 ? '' : 's'}` : rows);
  } else if (colCount) {
    parts.push(`${colCount} column${colCount === 1 ? '' : 's'}`);
  }

  if (d?.connector_name) parts.push(String(d.connector_name));

  const ms = typeof d?.elapsed_ms === 'number' ? d.elapsed_ms : null;
  if (ms !== null && ms > 0) {
    parts.push(ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);
  }

  return parts.join(' · ');
}

/** Whole seconds the current turn has been running, for the past-tense badge. */
function elapsedSecSinceStart(startedAt: number | null): number | undefined {
  if (!startedAt) return undefined;
  const sec = Math.round((Date.now() - startedAt) / 1000);
  return sec > 0 ? sec : undefined;
}

/**
 * A message id not already used in this chat.
 *
 * Ids are named after the time, but two messages can finish in the same
 * millisecond — the part of an answer before an approval pause and the part
 * after it, in a fast stream. The second then found the first by id, so its
 * text never showed. A clash gets a suffix; an unclashing id is unchanged.
 */
export function newMessageId(get: ChatGet, prefix: string): string {
  const base = `${prefix}-${Date.now()}`;
  const taken = new Set(get().messages.map((m) => m.id));
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/** Turn what has streamed so far into a finished assistant message. */
export function flushStreaming(get: ChatGet, auditId?: string | null) {
  const store = get();
  const statuses = store.streamingStatuses;
  if (store.streamingContent || statuses.length > 0) {
    const mid = newMessageId(get, 'assistant');
    store.addMessage({
      id: mid,
      role: 'assistant',
      content: store.streamingContent,
      timestamp: new Date().toISOString(),
      auditId: auditId || undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      durationSec: elapsedSecSinceStart(store.streamingStartedAt),
    });
    store.finalizeTimelineAssistant(mid);
    store.setStreamingContent('');
    store.setStreamingStatuses([]);
  } else {
    store.popTimelineStreamingIfStuck();
  }
  placeTurnNotes(get);
}

/** Put the notes that were waiting for the answer right after it. */
function placeTurnNotes(get: ChatGet) {
  const notes = get().pendingTurnNotes;
  if (!notes || notes.length === 0) return;
  get().setPendingTurnNotes([]);
  // The "definitions used" line reads as part of the answer, so it goes
  // straight under it, above any card asking to save what was learned.
  const ordered = [
    ...notes.filter((n) => n.type === 'definitions_used'),
    ...notes.filter((n) => n.type !== 'definitions_used'),
  ];
  for (const note of ordered) {
    get().addMessage(note);
    get().appendTimelineAssistantMessage(note.id);
  }
}

/**
 * A note that belongs under the answer. While the answer is still streaming it
 * waits, so it lands after the answer rather than above it.
 */
function addTurnNote(get: ChatGet, note: CopilotMessage) {
  const store = get();
  if (store.streamingContent || store.streamingStatuses.length > 0) {
    store.queueTurnNote(note);
    return;
  }
  store.addMessage(note);
  store.appendTimelineAssistantMessage(note.id);
}

/** Close a turn once its stream has ended, however it ended. */
export function endTurn(get: ChatGet) {
  const latest = get();
  const finalAuditId = latest.pendingAuditId;
  flushStreaming(get, finalAuditId);
  latest.setPendingAuditId(null);
  get().setIsStreaming(false);
}

/**
 * Close a turn the analyst stopped: keep what was said so far, marked as
 * stopped. Cancelling the request itself is the caller's job.
 */
export function stopTurn(get: ChatGet) {
  const store = get();
  const statuses = store.streamingStatuses;
  if (store.streamingContent || statuses.length > 0) {
    const sid = newMessageId(get, 'assistant');
    store.addMessage({
      id: sid,
      role: 'assistant',
      content: (store.streamingContent || '') + '\n\n*(Stopped by analyst)*',
      timestamp: new Date().toISOString(),
      auditId: store.pendingAuditId || undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      durationSec: elapsedSecSinceStart(store.streamingStartedAt),
    });
    store.finalizeTimelineAssistant(sid);
    store.setStreamingContent('');
    store.setStreamingStatuses([]);
  } else {
    store.popTimelineStreamingIfStuck();
  }
  placeTurnNotes(get);
  store.setPendingAuditId(null);
  store.setIsStreaming(false);
}

// ============================================================================
// Event processor - maps SSE events to store updates
// ============================================================================

export function processChatEvent(get: ChatGet, event: any, expectedThreadId: string | null) {
  const store = get();

  // Discard events that arrived after the user navigated to a different thread.
  // The SSE connection may still be open from a previous thread; without this
  // guard its events write into whatever thread is now current in the store.
  if (expectedThreadId && store.threadId && store.threadId !== expectedThreadId) {
    return;
  }

  if (store.isStreaming) {
    store.touchStreamingActivity();
  }

  switch (event.type) {
    case 'agent_message': {
      const content = event.data?.content || '';
      if (content) {
        store.setStreamingAction('');
        store.ensureTimelineStreamingAssistant();
        // kind === 'status' → mid-run narration (collapsible steps).
        // kind === 'final' (or missing, for back-compat) → the answer prose.
        if (event.data?.kind === 'status') {
          store.appendStreamingStatus(content);
        } else {
          store.appendStreamingContent(content);
        }
      }
      break;
    }

    case 'tool_call': {
      // Not rendered as a chat bubble, but it IS the deterministic spine of the
      // progress feed: tool_call always fires, whereas the model's narration
      // sentence is only a prompt rule and may not. Without this the feed is
      // empty on any turn where the model stays quiet.
      const toolName: string = event.data?.tool_name || '';
      const label = TOOL_ACTION_LABELS[toolName] || 'Working…';
      store.setStreamingAction(label);
      store.ensureTimelineStreamingAssistant();
      store.appendStreamingToolStep(label);
      store.noteStreamingToolCall();
      break;
    }

    case 'tool_result': {
      // SUPPRESSED from chat - tool results are not shown
      // (query_result events handle workspace DataGrid separately)
      break;
    }

    case 'query_result': {
      // Structured query result → create a workspace tab.
      const d = event.data || {};
      const tab: QueryResultTab = {
        id: `qr-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: 'query_result',
        hydration: 'ready',
        title: d.title || 'Query Result',
        frameId: d.frame_id || undefined,
        columns: d.columns || [],
        rows: d.rows || [],
        rowCount: d.row_count || 0,
        elapsedMs: d.elapsed_ms || 0,
        sql: d.sql || '',
        connectorId: d.connector_id || '',
        connectorName: d.connector_name || '',
        connectorType: d.connector_type || '',
        connectorCategory: d.connector_category || '',
        isFileConnector: d.is_file_connector || false,
        truncated: d.truncated || false,
        createdAt: new Date().toISOString(),
        columnTypes: d.column_types || undefined,
        columnSemantics: d.column_semantics || undefined,
        ...(event.event_id ? { eventId: event.event_id } : {}),
      };
      store.addWorkspaceTab(tab);
      store.appendTimelineArtifact(tab.id);
      // Deterministic receipt for the step that produced this result. Built
      // entirely from data already on the event — no model call, so it lands
      // the instant the query returns instead of waiting for the final answer.
      store.attachStreamingReceipt(formatResultReceipt(d));
      store.setStreamingAction('Analyzing results…');
      break;
    }

    case 'chart': {
      // Chart artifact → add to workspace as a ChartArtifact
      const c = event.data || {};
      const chartArtifact: ChartArtifact = {
        id: `chart-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: 'chart',
        hydration: 'ready',
        title: c.title || 'Chart',
        chartType: c.chart_intent?.type || c.type || 'bar',
        spec: c.spec || {},
        rowCount: c.row_count || 0,
        columns: c.columns || [],
        chartIntent: c.chart_intent || {},
        createdAt: new Date().toISOString(),
        eventId: event.event_id || undefined,
      };
      store.addWorkspaceTab(chartArtifact);
      store.appendTimelineArtifact(chartArtifact.id);
      store.setStreamingAction('');
      break;
    }

    case 'forecast': {
      store.setStreamingAction('');
      break;
    }

    case 'metric_card': {
      const m = event.data || {};
      const metricArtifact: MetricArtifact = {
        id: `metric-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: 'metric_card',
        hydration: 'ready',
        title: m.title || m.metric_name || 'Metric',
        metricName: m.metric_name || m.title || '',
        metricValue: m.metric_value,
        columns: m.columns || [],
        rowCount: m.row_count || 0,
        createdAt: new Date().toISOString(),
        eventId: event.event_id || undefined,
      };
      store.addWorkspaceTab(metricArtifact);
      store.appendTimelineArtifact(metricArtifact.id);
      store.setStreamingAction('');
      break;
    }

    case 'document_ready': {
      const d = event.data || {};
      const docArtifact: DocumentArtifact = {
        id: `doc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: 'document',
        hydration: 'ready',
        title: d.title || 'Agent Report',
        playbookId: d.playbook_id || '',
        runId: d.run_id || '',
        artifactId: d.artifact_id || undefined,
        role: d.role || undefined,
        format: d.format || 'docx',
        createdAt: new Date().toISOString(),
      };
      store.addWorkspaceTab(docArtifact);
      store.appendTimelineArtifact(docArtifact.id);
      store.setStreamingAction('');
      break;
    }

    case 'schema_loaded': {
      // Populate the Data Explorer schema tree (map snake_case → camelCase)
      const rawConnectors = event.data?.connectors || [];
      const mapped = rawConnectors.map((c: any) => ({
        connectorId: c.connector_id || c.connectorId || '',
        name: c.name || '',
        type: c.type || '',
        tables: (c.tables || []).map((t: any) => ({
          name: t.name || '',
          columns: (t.columns || []).map((col: any) =>
            typeof col === 'string' ? { name: col, type: '' } : { name: col.name || '', type: col.type || '' }
          ),
          rowCount: t.row_count || t.rowCount,
        })),
      }));
      store.setSchemaTree(mapped);
      break;
    }

    case 'thread_title':
    case 'suggested_title': {
      // Sent at the top level today; older streams nested it under `data`.
      const title = String(event.data?.title || event.title || '').trim();
      if (title) {
        store.setThreadTitle(title);
      }
      break;
    }

    case 'sql_approval': {
      // Flush streaming content, show inline workspace approval card
      flushStreaming(get);
      const sqlAprId = `sql-apr-${Date.now()}`;
      store.appendTimelineSqlApproval(sqlAprId);
      store.setPendingSQLApproval({
        sql: event.data?.sql || '',
        title: event.data?.title || 'Query',
        connectorId: event.data?.connector_id || '',
        connectorName: event.data?.connector_name || '',
        connectorType: event.data?.connector_type || '',
        connectorCategory: event.data?.connector_category || '',
        sessionId: event.session_id || event.data?.session_id || '',
        isFileConnector: event.data?.is_file_connector || false,
        isApiConnector: event.data?.connector_category === 'api',
        entityId: event.data?.entity_id || '',
        entityName: event.data?.entity_name || '',
        columns: event.data?.columns || [],
        resource: event.data?.resource || '',
        properties: event.data?.properties || [],
        limit: event.data?.limit ?? null,
        timelineId: sqlAprId,
      });
      store.setIsStreaming(false);
      break;
    }

    case 'workbook_ready': {
      // A workbook the agent built and saved. The bytes are stored, so this
      // card keeps working long after the session's tables are gone.
      flushStreaming(get);
      const workbook = toWorkbook(event.data || {});
      if (workbook.workbookId) {
        store.addWorkbook(workbook);
        store.appendTimelineWorkbook(workbook.id);
      }
      store.setStreamingAction('');
      break;
    }

    case 'download_ready': {
      // The agent lined up a file. Nothing is fetched until someone clicks —
      // the download is authenticated as them, not as the agent.
      flushStreaming(get);
      const offer = toDownloadOffer(
        event.data || {},
        `dl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      );
      if (offer.frameId) {
        store.addDownloadOffer(offer);
        store.appendTimelineDownload(offer.id);
      }
      store.setStreamingAction('');
      break;
    }

    case 'voice_call': {
      // The call is over. Leave a card in the chat that outlives the modal.
      flushStreaming(get);
      const summary = toVoiceCallSummary(event.data || {});
      if (summary.callId) {
        store.addVoiceCallSummary(summary);
        store.appendTimelineVoiceCall(summary.callId);
      }
      break;
    }

    case 'voice_call_approval': {
      // Nothing is dialed until someone approves. Flush what the agent was
      // saying and drop an approval card into the timeline.
      flushStreaming(get);
      const callAprId = `call-apr-${Date.now()}`;
      store.appendTimelineVoiceCallApproval(callAprId);
      store.setPendingVoiceCallApproval({
        carrierName: event.data?.carrier_name || 'the carrier',
        phoneNumber: event.data?.phone_number || '',
        objective: event.data?.objective || '',
        policyNumber: event.data?.policy_number || undefined,
        policyholder: event.data?.policyholder || undefined,
        knownFields: event.data?.known_fields || [],
        factsNote: event.data?.facts_note || undefined,
        numberIsDefault: Boolean(event.data?.number_is_default),
        sessionId: event.session_id,
        timelineId: callAprId,
      });
      store.setIsStreaming(false);
      break;
    }

    case 'finding': {
      const f = event.data || {};
      store.addFinding({
        id: f.id || `finding-${Date.now()}`,
        title: f.title || 'Untitled Finding',
        narrative: f.narrative || '',
        dataPoint: f.data_point || {},
        artifactRefs: f.artifact_refs || [],
        createdAt: f.created_at || new Date().toISOString(),
      });
      store.setStreamingAction('');
      break;
    }

    case 'knowledge_captured': {
      flushStreaming(get);
      const rawData = event.data;
      let kcNodes: Array<{
        name: string;
        node_type: string;
        definition?: string;
        sql?: string;
        node_id?: string;
      }> = [];
      if (Array.isArray(rawData)) {
        kcNodes = rawData;
      } else if (rawData && typeof rawData === 'object') {
        // Could be {name, node_type, ...} or {items: [...]} or {data: {name, ...}}
        if (rawData.items && Array.isArray(rawData.items)) {
          kcNodes = rawData.items;
        } else if (rawData.name) {
          kcNodes = [rawData];
        } else if (rawData.data && rawData.data.name) {
          kcNodes = [rawData.data];
        }
      }

      if (kcNodes.length > 0) {
        const kcId = newMessageId(get, 'knowledge');
        store.addMessage({
          id: kcId,
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          type: 'knowledge_captured',
          knowledgeItems: kcNodes.map((kc) => ({
            name: kc.name,
            node_type: kc.node_type,
            definition: kc.definition,
            sql: kc.sql,
            node_id: kc.node_id,
          })),
        });
        store.appendTimelineAssistantMessage(kcId);
      }
      break;
    }

    case 'definitions_learned': {
      const items = toLearnedItems(event.data?.items);
      if (items.length === 0) break;
      addTurnNote(get, {
        id: newMessageId(get, 'definitions-learned'),
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        type: 'definitions_learned',
        definitionsEventId: event.event_id || undefined,
        definitionItems: items,
      });
      break;
    }

    // Saved with the chat so a resumed turn can finish its card; nothing to draw.
    case 'definitions_pending':
      break;

    case 'definitions_used': {
      const used = toDefinitionsUsed(event.data?.items);
      if (used.length === 0) break;
      addTurnNote(get, {
        id: newMessageId(get, 'definitions-used'),
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        type: 'definitions_used',
        definitionsUsed: used,
      });
      break;
    }

    case 'context_usage': {
      const cu = event.data || {};
      store.setContextUsage({
        used: cu.used || 0,
        budget: cu.budget || 120000,
        pct: cu.pct || 0,
        trimmed: cu.trimmed || false,
        trimmedCount: cu.trimmed_count || 0,
      });
      break;
    }

    case 'turn_complete': {
      const auditId = store.pendingAuditId;
      flushStreaming(get, auditId);
      store.setPendingAuditId(null);
      store.setIsStreaming(false);
      break;
    }

    case 'calculation_audit': {
      const report = toAuditReport(event, store.threadId);
      const storedId = store.addAuditReport(report, report.id);
      store.setPendingAuditId(storedId);
      break;
    }

    case 'error': {
      const eid = newMessageId(get, 'error');
      store.addMessage({
        id: eid,
        role: 'assistant',
        content: `⚠️ ${event.message || event.data?.message || 'An error occurred'}`,
        timestamp: new Date().toISOString(),
      });
      store.appendTimelineAssistantMessage(eid);
      store.setIsStreaming(false);
      break;
    }

    default:
      console.log('[chat] Unhandled event:', event.type, event);
  }
}

/** The backend's calculation audit, in the shape the audit drawer reads. */
function toAuditReport(event: any, currentThreadId: string | null): CalculationAuditReport {
  const raw = event.data || {};
  const reportId = event.event_id || raw.id || `audit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  return {
    id: reportId,
    threadId: raw.thread_id || currentThreadId,
    question: raw.question || '',
    startedAt: raw.started_at || event.timestamp || new Date().toISOString(),
    completedAt: raw.completed_at || event.timestamp || new Date().toISOString(),
    showAuditLink: typeof raw.show_audit_link === 'boolean' ? raw.show_audit_link : undefined,
    dataSources: (raw.data_sources || []).map((s: any) => ({
      frameId: s.frame_id || '',
      title: s.title || 'Query Result',
      connectorId: s.connector_id || '',
      connectorName: s.connector_name || '',
      connectorType: s.connector_type || '',
      rowCount: s.row_count || 0,
      columns: s.columns || [],
      sql: s.sql || '',
      timestamp: s.timestamp || event.timestamp || new Date().toISOString(),
    })),
    steps: (raw.steps || []).map((st: any) => ({
      type: st.type || '',
      toolName: st.tool_name || '',
      toolArgs: st.tool_args || {},
      summary: st.summary || '',
      timestamp: st.timestamp || event.timestamp || new Date().toISOString(),
    })),
    charts: (raw.charts || []).map((c: any) => ({
      title: c.title || 'Chart',
      chartType: c.chart_type || 'chart',
      rowCount: c.row_count || 0,
      columns: c.columns || [],
      timestamp: c.timestamp || event.timestamp || new Date().toISOString(),
    })),
    story: raw.story
      ? {
          headline: raw.story.headline || '',
          dataSources: raw.story.data_sources
            ? {
                summary: raw.story.data_sources.summary || '',
                connectors: raw.story.data_sources.connectors || [],
                tablesOrEndpoints: raw.story.data_sources.tables_or_endpoints || [],
                details: (raw.story.data_sources.details || []).map((d: any) => ({
                  name: d.name || '',
                  connectorType: d.connector_type || '',
                  records: d.records || 0,
                  columns: d.columns || 0,
                  frameId: d.frame_id || '',
                  query: d.query || '',
                  requestType: d.request_type || 'sql',
                  tablesOrEndpoints: d.tables_or_endpoints || [],
                })),
              }
            : undefined,
          staging: raw.story.staging
            ? {
                summary: raw.story.staging.summary || '',
                stagedRecords: raw.story.staging.staged_records || 0,
                frameCount: raw.story.staging.frame_count || 0,
              }
            : undefined,
          calculation: raw.story.calculation
            ? {
                summary: raw.story.calculation.summary || '',
                sqlStepCount: raw.story.calculation.sql_step_count || 0,
                finalSql: raw.story.calculation.final_sql || '',
                steps: (raw.story.calculation.steps || []).map((step: any) => ({
                  tool: step.tool || '',
                  sql: step.sql || '',
                })),
              }
            : undefined,
          outputs: raw.story.outputs
            ? {
                summary: raw.story.outputs.summary || '',
                resultTypes: raw.story.outputs.result_types || [],
                chartCount: raw.story.outputs.chart_count || 0,
                chartTitles: raw.story.outputs.chart_titles || [],
                chartDetails: (raw.story.outputs.chart_details || []).map((c: any) => ({
                  title: c.title || '',
                  type: c.type || '',
                  records: c.records || 0,
                  columns: c.columns || [],
                  xFields: c.x_fields || [],
                  yFields: c.y_fields || [],
                  timeUnit: c.time_unit || '',
                })),
                forecastCount: raw.story.outputs.forecast_count ?? 0,
                forecastDetails: (raw.story.outputs.forecast_details || []).map((f: any) => ({
                  title: f.title || '',
                  frameId: f.frame_id || '',
                  selectedModel: f.selected_model || '',
                  horizon: f.horizon ?? 0,
                  timeUnit: f.time_unit || '',
                  confidenceLevel: f.confidence_level,
                  confidenceLevelPct: f.confidence_level_pct,
                  reliability: f.reliability || '',
                  historyPoints: f.history_points ?? 0,
                  targetColumn: f.target_column || '',
                  timeColumn: f.time_column || '',
                  periodsCount: f.periods_count ?? 0,
                  firstPeriod: f.first_period
                    ? {
                        period: f.first_period.period,
                        prediction: f.first_period.prediction,
                        lower: f.first_period.lower,
                        upper: f.first_period.upper,
                      }
                    : undefined,
                  candidateModels: (f.candidate_models || []).map((m: any) => ({
                    model: m.model || '',
                    mae: m.mae ?? null,
                    rmse: m.rmse ?? null,
                    wape: m.wape ?? null,
                    p95AbsError: m.p95_abs_error ?? null,
                    selectionScore: m.selection_score ?? null,
                  })),
                  selectedModelMetrics: f.selected_model_metrics
                    ? {
                        mae: f.selected_model_metrics.mae ?? null,
                        rmse: f.selected_model_metrics.rmse ?? null,
                        wape: f.selected_model_metrics.wape ?? null,
                        p95AbsError: f.selected_model_metrics.p95_abs_error ?? null,
                        selectionScore: f.selected_model_metrics.selection_score ?? null,
                      }
                    : undefined,
                  caveats: f.caveats || [],
                  dataWindowStart: f.data_window_start || '',
                  dataWindowEnd: f.data_window_end || '',
                  droppedPoints: f.dropped_points ?? 0,
                  rawRows: f.raw_rows ?? 0,
                  preparedPoints: f.prepared_points ?? 0,
                  seasonLength: f.season_length ?? 0,
                })),
              }
            : undefined,
        }
      : undefined,
  };
}
